// Sparkline SVG Chart Component

export interface SparklineOptions {
    data: Array<number | null>;
    width?: number;
    height?: number;
    color?: string;
}

/**
 * Render an inline SVG sparkline chart
 */
export function renderSparkline(container: HTMLElement, options: SparklineOptions): void {
    const { data, width = 250, height = 60, color = '#8b7355' } = options;

    if (!data || data.length < 2) {
        container.innerHTML = '<span style="opacity: 0.5; font-size: 0.8rem;">Insufficient data</span>';
        return;
    }

    // Calculate min/max for normalization
    const validData = data.filter((v): v is number => v !== null && !isNaN(v));
    if (validData.length === 0) {
        container.innerHTML = '<span style="opacity: 0.5; font-size: 0.8rem;">No data</span>';
        return;
    }

    const min = Math.min(...validData);
    const max = Math.max(...validData);
    const range = max - min || 1; // Avoid division by zero

    // Padding
    const padding = 5;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    // Generate path points
    const stepX = chartWidth / (data.length - 1);
    const points = data.map((value, index) => {
        if (value === null || isNaN(value)) return null;
        const x = padding + index * stepX;
        const y = padding + chartHeight - ((value - min) / range) * chartHeight;
        return { x, y };
    }).filter(p => p !== null);

    // Build SVG path
    const pathData = points.map((p, i) =>
        `${i === 0 ? 'M' : 'L'} ${p!.x} ${p!.y}`
    ).join(' ');

    // Build area path (for subtle fill)
    const areaData = `
    ${pathData}
    L ${points[points.length - 1]!.x} ${height - padding}
    L ${points[0]!.x} ${height - padding}
    Z
  `;

    // Render SVG
    container.innerHTML = `
    <svg class="sparkline" width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <path class="sparkline__area" d="${areaData}" />
      <path class="sparkline__path" d="${pathData}" />
    </svg>
  `;
}
